class WordSearchGenerator {
  static generatePuzzle(wordString, secretWord, gridSize, difficultyFactor = 1, maxAttempts = 100) {
    // Split by any whitespace or non-alphanumeric character
    let words = wordString.split(/[^a-zA-Z0-9]+/).filter(w => w.length > 0).map(w => w.toUpperCase());
    
    const secretUpper = secretWord.trim().toUpperCase();
    words.push(secretUpper);
    
    let grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(' '));
    
    let directionUsage = {};
    
    words.sort((a, b) => b.length - a.length);
    words = this.shuffleArray(words);
    
    let wordPlacements = [];
    
    let bestGrid = null;
    let bestPlacements = [];
    
    for (let iteration = 0; iteration < maxAttempts; iteration++) {
      grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(' '));
      directionUsage = {};
      wordPlacements = [];
      
      words = this.shuffleArray(words);
      let allPlaced = true;
      
      for (const word of words) {
        const placement = this.placeWord(word, grid, gridSize, difficultyFactor, directionUsage, maxAttempts);
        if (placement === null) {
          allPlaced = false;
          break;
        }
        wordPlacements.push(placement);
      }
      
      if (allPlaced) {
        bestGrid = grid.map(row => [...row]);
        bestPlacements = [...wordPlacements];
        break;
      }
    }
    
    if (bestGrid === null) {
      console.error('Puzzle generation failed: Could not place all words.');
      return null;
    }
    
    grid = bestGrid;
    
    this.fillEmptyCells(grid, gridSize, words, difficultyFactor);
    
    let puzzleString = '';
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        puzzleString += grid[y][x];
      }
    }
    
    return this.generateJsonOutput(puzzleString, bestPlacements, secretUpper, gridSize);
  }
  
  static placeWord(word, grid, gridSize, difficultyFactor, directionUsage, maxAttempts) {
    const directions = [
      { dx: 1, dy: 0, name: 'horizontalForward' },
      { dx: 0, dy: 1, name: 'verticalDown' }
    ];
    
    if (difficultyFactor >= 1) {
      directions.push({ dx: 1, dy: -1, name: 'diagonalUpForward' });
      directions.push({ dx: 1, dy: 1, name: 'diagonalDownForward' });
    }
    
    if (difficultyFactor >= 2) {
      directions.push({ dx: -1, dy: 0, name: 'horizontalBackward' });
      directions.push({ dx: 0, dy: -1, name: 'verticalUp' });
    }
    
    if (difficultyFactor >= 3) {
      directions.push({ dx: -1, dy: -1, name: 'diagonalUpBackward' });
      directions.push({ dx: -1, dy: 1, name: 'diagonalDownBackward' });
    }
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const row = Math.floor(Math.random() * gridSize);
      const col = Math.floor(Math.random() * gridSize);
      
      const usageCounts = directions.map(dir => ({
        dir,
        count: directionUsage[dir.name] || 0
      }));
      
      const minCount = Math.min(...usageCounts.map(u => u.count));
      
      const leastUsed = usageCounts.filter(u => u.count === minCount).map(u => u.dir);
      
      const chosenDir = leastUsed[Math.floor(Math.random() * leastUsed.length)];
      const { dx, dy, name: dirName } = chosenDir;
      
      if (this.canPlaceWord(word, row, col, dx, dy, grid, gridSize)) {
        for (let i = 0; i < word.length; i++) {
          const x = col + i * dx;
          const y = row + i * dy;
          grid[y][x] = word[i];
        }
        
        directionUsage[dirName] = (directionUsage[dirName] || 0) + 1;
        
        const startIndex = row * gridSize + col;
        const endX = col + (word.length - 1) * dx;
        const endY = row + (word.length - 1) * dy;
        const endIndex = endY * gridSize + endX;
        
        return {
          word,
          startIndex,
          endIndex,
          direction: dirName
        };
      }
    }
    
    return null;
  }
  
  static canPlaceWord(word, row, col, dx, dy, grid, gridSize) {
    for (let i = 0; i < word.length; i++) {
      const x = col + i * dx;
      const y = row + i * dy;
      
      if (x < 0 || x >= gridSize || y < 0 || y >= gridSize) {
        return false;
      }
      
      if (grid[y][x] !== ' ' && grid[y][x] !== word[i]) {
        return false;
      }
    }
    
    return true;
  }
  
  static fillEmptyCells(grid, gridSize, words, difficultyFactor) {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let letterPairs = [];
    
    if (difficultyFactor === 3) {
      letterPairs = this.collectLetterPairs(words);
    }
    
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        if (grid[y][x] === ' ') {
          if (difficultyFactor < 3 || letterPairs.length === 0) {
            grid[y][x] = alphabet[Math.floor(Math.random() * alphabet.length)];
          } else {
            const pair = letterPairs[Math.floor(Math.random() * letterPairs.length)];
            grid[y][x] = pair[Math.floor(Math.random() * 2)];
          }
        }
      }
    }
  }
  
  static collectLetterPairs(words) {
    const allLetters = [];
    for (const word of words) {
      for (let i = 0; i < word.length; i++) {
        allLetters.push(word[i]);
      }
    }
    
    const pairs = [];
    for (let i = 0; i < allLetters.length - 1; i++) {
      pairs.push(allLetters[i] + allLetters[i + 1]);
    }
    
    return pairs;
  }
  
  static generateJsonOutput(puzzleString, placements, secretWord, gridSize, customDate = null, joke = null) {
    const date = customDate || new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const key = `wordsearch_${date}`;
    
    const wordsArray = [];
    let secretData = '';
    
    for (const placement of placements) {
      const wordEntry = `${placement.word},${placement.startIndex},${placement.endIndex}`;
      if (placement.word === secretWord) {
        secretData = wordEntry;
      } else {
        wordsArray.push(wordEntry);
      }
    }
    
    const jsonData = {
      secret: secretData,
      words: wordsArray,
      ...(joke && { joke }),
      date,
      size: gridSize,
      puzzle: puzzleString
    };
    
    return {
      key,
      data: jsonData,
      fullJson: { [key]: jsonData }
    };
  }
  
  static shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}

export default WordSearchGenerator;
